<template>
  <NavigationLayout
    :navigation-title="title"
    :navigation-bar-style="navigationBarStyle"
    :navigation-content-style="navigationContentStyle"
    v-if="!isLoading"
  >
    <div v-if="!showNotFound()" slot="contents">
      <div class="vip_point_history_container">
        <div class="header">
          <div class="header_title">
            {{ $t('chargeGradeVipPointHistoryTitle') }}
          </div>
        </div>
        <div class="history_list">
          <div
            v-for="history in histories"
            :key="history.id"
            class="history_item"
          >
            <div class="date">{{ formatDate(history.createdAt) }}</div>
            <div class="history_detail">
              <div class="entry_name">{{ history.entryName }}</div>
              <div class="value" :class="{ negative: history.value < 0 }">
                {{ history.value > 0 ? '' : '-'
                }}{{ formatNumber(Math.abs(history.value)) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <not-found
      v-show="showNotFound()"
      class="vip_point_history_notfound"
      slot="contents"
    />
  </NavigationLayout>
</template>

<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator'
import { LanguageQueryHandler } from '@/mixins/LanguageQuery'
import NavigationLayout from '@/components/NavigationLayout.vue'
import { api } from '@/api/appGatewayServerApi'
import { checkInMaintenance } from '@/utils/maintenance'
import { handleUnauthorized } from '@/utils/handle_error'
import { ApiError } from '@/api/error'
import NotFound from '@/components/Error/NotFound.vue'
import * as models from '@/models'
import { getTokenFromCookie } from '@/utils/token'

@Component({
  components: {
    NotFound,
    NavigationLayout,
  },
})
export default class ChargeGradeVipPointHistory extends Mixins(
  LanguageQueryHandler
) {
  histories: models.ChargeGradeVipPointHistories = []
  isNotFound: boolean = false
  isLoading: boolean = false

  created() {
    this.fetchData()
  }

  async fetchData() {
    this.isLoading = true
    try {
      // 表示時にも一旦ポイントを失効させる
      await api().charge_grade_web.vip_point.expire()

      // VIPポイント履歴の取得
      const historyResponse =
        await api().charge_grade_web.vip_point_history.get()
      this.histories = historyResponse.histories
    } catch (error) {
      if (checkInMaintenance(error)) {
        return
      }

      if (!handleUnauthorized(error)) {
        this.isNotFound = true
      }
    } finally {
      this.isLoading = false
    }
  }

  formatDate(date: Date) {
    // サーバーから受け取った時刻（UTC）をDateオブジェクトに変換
    const utcDate = new Date(date)

    // 出力例（ユーザーのローカル時間で表示）：
    // 日本語（ja）    : 2024年5月27日 14時30分
    // 英語（en）      : May 27, 2024, 2:30 PM
    // 韓国語（ko）    : 2024년 5월 27일 오후 2시 30분
    // 中国語（zh-hant）: 2024年5月27日 下午2:30
    return utcDate.toLocaleString(this.$i18n.locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: this.$i18n.locale === 'en',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, // ユーザーのローカルタイムゾーンを使用
    })
  }

  showNotFound() {
    return this.isNotFound && !this.isLoading
  }

  isWebView() {
    return getTokenFromCookie() !== undefined
  }

  formatNumber(value: number): string {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  get title() {
    // nullをわたすとタイトルロゴが表示され、stringを渡すとその文字列が表示される
    return this.showNotFound() || this.isWebView() ? '' : null
  }
  get navigationContentStyle() {
    if (this.isWebView()) {
      return {
        'padding-top': 'var(--safe-area-inset-top, 0px)',
      }
    }
    return {
      'padding-top': '44px',
    }
  }
  get navigationBarStyle() {
    if (this.isWebView()) {
      return {
        'flex-direction': 'row',
        'justify-content': 'center',
        'z-index': '2',
        'top': 'var(--safe-area-inset-top, 0px)',
        height: '0px',
      }
    }
    return {
      'flex-direction': 'row',
      'justify-content': 'center',
      'z-index': '2',
      'border-bottom': '1px solid #e8e8e8',
      height: '44px',
    }
  }
}
</script>

<style scoped>
.vip_point_history_container {
  position: relative;
  width: 100%;
  min-height: 100vh;
  background: #ffffff;
  padding: 0 0 var(--safe-area-inset-bottom, 0px) 0;
}

.header {
  width: 375px;
  height: 44px;
  margin: 0 auto;
  position: relative;
  background: #ffffff;
}

.header_title {
  position: absolute;
  min-width: min-content; /* コンテンツの最小幅を確保 */
  height: 26px;
  left: 50%;
  bottom: 8px;
  transform: translateX(-50%); /* 中央寄せの調整 */
  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 17px;
  line-height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #262626;
  white-space: nowrap; /* タイトルが途中で改行されないように */
}

.history_list {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.history_item {
  width: 100%;
  min-height: 93px;
  background: #ffffff;
  position: relative;
  padding: 16px;
  box-sizing: border-box;
  border-bottom: 0.5px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.date {
  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 300;
  font-size: 12px;
  line-height: 130%;
  color: #808080;
}

.history_detail {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.entry_name {
  flex: 1;
  margin-right: 16px;
  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  color: #262626;
}

.value {
  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 300;
  font-size: 12px;
  line-height: 16px;
  text-align: right;
  color: #2b2b2b;
  white-space: nowrap;
}

.value.negative {
  color: #7b7b7b;
}

.vip_point_history_notfound {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
}

/* スマートフォン向けの調整 */
@media screen and (max-width: 375px) {
  .history_item {
    padding: 12px;
  }

  .entry_name {
    font-size: 13px;
  }

  .value {
    font-size: 13px;
  }

  .date {
    font-size: 11px;
  }
}
</style>
